//@flow
import React, {useMemo, useState} from 'react';
import {
  makeStyles,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
} from '@material-ui/core';
import {Add, Delete, Edit, Close, Check} from '@material-ui/icons';

import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import ConfirmModal from '../../../../../elements/Modals/ConfirmModal';
import Toast from '../../../../../elements/Toast';
import {colors} from '../../../../../assets/styles/colors';

import {
  useBlogCategories,
  useBlogCategoriesAction,
} from '../../../utils/blog-category';

type ToastAlert = {
  message: string,
  severity: 'success' | 'error' | 'info' | 'warning',
};

type Props = {
  hasBlogFeature: boolean,
  onTierRestriction: () => void,
};

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: colors.grey800,
  },
  subtitle: {
    fontSize: '0.875rem',
    color: colors.grey600,
  },
  createRow: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'stretch',
    maxWidth: 600,
  },
  createButton: {
    width: 56,
    borderRadius: 8,
    border: `1px solid ${colors.grey300}`,
    background: 'white',
  },
  list: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: '1rem',
    marginTop: '0.5rem',
  },
  card: {
    border: `1px solid ${colors.grey200}`,
    borderRadius: 8,
    padding: '0.75rem 1rem',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'all 0.2s ease',
    backgroundColor: 'white',
    position: 'relative',
    '&:hover': {
      borderColor: colors.primary,
      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
      '& $actions': {
        opacity: 1,
      },
    },
  },
  cardEditing: {
    borderColor: colors.primary,
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  },
  rowName: {
    fontSize: '0.95rem',
    fontWeight: 500,
    color: colors.grey800,
    wordBreak: 'break-word',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    opacity: 0,
    transition: 'opacity 0.2s ease',
    marginLeft: '0.5rem',
  },
  actionsVisible: {
    opacity: 1,
  },
  divider: {
    height: 16,
    margin: '0 0.25rem',
  },
  spinner: {
    width: 18,
    height: 18,
  },
}));

export default function BlogCategoryManager(props: Props) {
  const {hasBlogFeature, onTierRestriction} = props;

  const classes = useStyles();

  const categories = useBlogCategories();
  const {
    createCategory,
    updateCategory,
    deleteBlogCategory,
    loading,
  } = useBlogCategoriesAction();

  const [toast, setToast] = useState<ToastAlert | null>(null);

  const [newCategoryName, setNewCategoryName] = useState('');

  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(
    null
  );
  const [editingValue, setEditingValue] = useState('');

  const [deleteCategoryId, setDeleteCategoryId] = useState<string | null>(null);

  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => a.name.localeCompare(b.name));
  }, [categories]);

  const closeToast = () => {
    setToast(null);
  };

  const showError = (message: string) => {
    setToast({
      message,
      severity: 'error',
    });
  };

  const showSuccess = (message: string) => {
    setToast({
      message,
      severity: 'success',
    });
  };

  const requireFeature = () => {
    if (hasBlogFeature) return true;
    onTierRestriction();
    return false;
  };

  const onCreate = async () => {
    if (!requireFeature()) return;

    const name = newCategoryName.trim();
    if (!name) {
      showError('Ingresa un nombre de categoría');
      return;
    }

    try {
      await createCategory({name});
      setNewCategoryName('');
      showSuccess('Categoría creada');
    } catch (e) {
      showError('Ocurrió un error al crear la categoría');
    }
  };

  const startEditing = (id: string, currentName: string) => {
    if (!requireFeature()) return;

    setEditingCategoryId(id);
    setEditingValue(currentName);
  };

  const cancelEditing = () => {
    setEditingCategoryId(null);
    setEditingValue('');
  };

  const onSaveEditing = async (id: string) => {
    if (!requireFeature()) return;

    const name = editingValue.trim();
    if (!name) {
      showError('El nombre no puede estar vacío');
      return;
    }

    const original = categories.find(c => c.id === id);
    const originalName = original ? original.name : '';

    if (name === originalName) {
      cancelEditing();
      return;
    }

    try {
      await updateCategory(id, {name});
      cancelEditing();
      showSuccess('Categoría actualizada');
    } catch (e) {
      showError('Ocurrió un error al actualizar la categoría');
    }
  };

  const requestDelete = (id: string) => {
    if (!requireFeature()) return;
    setDeleteCategoryId(id);
  };

  const cancelDelete = () => {
    setDeleteCategoryId(null);
  };

  const onConfirmDelete = async () => {
    if (!deleteCategoryId) return;

    try {
      await deleteBlogCategory(deleteCategoryId);
      setDeleteCategoryId(null);
      if (editingCategoryId === deleteCategoryId) {
        cancelEditing();
      }
      showSuccess('Categoría eliminada');
    } catch (e) {
      showError('Ocurrió un error al eliminar la categoría');
    }
  };

  let countSuffix = 'orías';
  if (categories.length === 1) countSuffix = 'oría';
  const countText = `${categories.length} categ${countSuffix}`;

  return (
    <section className={classes.root}>
      <ConfirmModal
        open={!!deleteCategoryId}
        onClose={cancelDelete}
        onCancel={cancelDelete}
        onConfirm={onConfirmDelete}
        message="¿Estás seguro que deseas eliminar esta categoría?"
      />

      <div className={classes.header}>
        <div>
          <Typography className={classes.title}>Categorías</Typography>
          <Typography className={classes.subtitle}>{countText}</Typography>
        </div>

        {loading ? <CircularProgress className={classes.spinner} /> : null}
      </div>

      <div className={classes.createRow}>
        <CustomTextField
          label="Nueva categoría"
          value={newCategoryName}
          onChange={e => {
            setNewCategoryName(e.target.value);
          }}
          onKeyDown={e => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            onCreate();
          }}
          disabled={loading}
        />

        <IconButton
          className={classes.createButton}
          onClick={onCreate}
          disabled={loading}
        >
          <Add />
        </IconButton>
      </div>

      <div className={classes.list}>
        {sortedCategories.map(category => {
          const isEditing = editingCategoryId === category.id;

          let content;
          let actions;

          if (isEditing) {
            content = (
              <CustomTextField
                value={editingValue}
                onChange={e => {
                  setEditingValue(e.target.value);
                }}
                onKeyDown={e => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  onSaveEditing(category.id);
                }}
                disabled={loading}
                autoFocus
              />
            );

            actions = (
              <>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onSaveEditing(category.id)}
                  disabled={loading}
                >
                  <Check fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={cancelEditing}
                  disabled={loading}
                >
                  <Close fontSize="small" />
                </IconButton>
              </>
            );
          } else {
            content = (
              <Typography className={classes.rowName}>
                {category.name}
              </Typography>
            );

            actions = (
              <>
                <IconButton
                  size="small"
                  onClick={() => startEditing(category.id, category.name)}
                  disabled={loading}
                >
                  <Edit fontSize="small" />
                </IconButton>
                <Divider className={classes.divider} orientation="vertical" />
                <IconButton
                  size="small"
                  onClick={() => requestDelete(category.id)}
                  disabled={loading}
                >
                  <Delete fontSize="small" color="error" />
                </IconButton>
              </>
            );
          }

          return (
            <div
              key={category.id}
              className={`${classes.card} ${
                isEditing ? classes.cardEditing : ''
              }`}
            >
              <div style={{flex: 1}}>{content}</div>
              <div
                className={`${classes.actions} ${
                  isEditing ? classes.actionsVisible : ''
                }`}
              >
                {actions}
              </div>
            </div>
          );
        })}
      </div>

      <Toast open={!!toast} onClose={closeToast} severity={toast?.severity}>
        {toast?.message}
      </Toast>
    </section>
  );
}
